import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyTournaments } from '../services/tournamentService';
import { useAuth } from '../context/AuthContext';
import { Clock, Trophy, Crosshair, Eye } from 'lucide-react';
import TCoinIcon from '../components/TCoinIcon';

type Tab = 'upcoming' | 'live' | 'completed';

export default function MyTournaments() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [tab, setTab] = useState<Tab>('live');
    const { tournaments: realTournaments } = useMyTournaments(user?.uid);

    const myTournaments = realTournaments;
    const filtered = myTournaments.filter(t => t.status === tab);

    return (
        <div className="page-wrapper" style={{ maxWidth: 800 }}>
            <h1 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.8rem', marginBottom: 4 }} className="animate-in">🏆 My Tournaments</h1>
            <p style={{ color: 'var(--text-muted)', marginBottom: 24 }} className="animate-in">Track your matches and results</p>

            <div className="tabs animate-in">
                <button className={`tab-btn ${tab === 'upcoming' ? 'active' : ''}`} onClick={() => setTab('upcoming')}>
                    Upcoming ({myTournaments.filter(t => t.status === 'upcoming').length})
                </button>
                <button className={`tab-btn ${tab === 'live' ? 'active' : ''}`} onClick={() => setTab('live')}>
                    Live ({myTournaments.filter(t => t.status === 'live').length})
                </button>
                <button className={`tab-btn ${tab === 'completed' ? 'active' : ''}`} onClick={() => setTab('completed')}>
                    Completed ({myTournaments.filter(t => t.status === 'completed').length})
                </button>
            </div>

            {filtered.length === 0 ? (
                <div className="card animate-in" style={{ textAlign: 'center', padding: 48 }}>
                    <p style={{ fontSize: '2rem', marginBottom: 8 }}>📋</p>
                    <p style={{ color: 'var(--text-muted)' }}>No {tab} tournaments.</p>
                </div>
            ) : (
                filtered.map((mt, i) => (
                    <div key={mt.id} className={`card animate-in stagger-${i + 1}`} style={{ marginBottom: 12, position: 'relative', overflow: 'hidden' }}>
                        {mt.status === 'live' && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'var(--success)' }} />}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, paddingTop: mt.status === 'live' ? 4 : 0 }}>
                            <div>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                                    <span className={`tc-game-badge ${mt.game === 'PUBG' ? 'pubg' : mt.game === 'Free Fire' ? 'freefire' : 'wildrift'}`}>{mt.game}</span>
                                    <span className={`tc-status ${mt.status}`}>
                                        {mt.status === 'live' && <span className="pulse-dot" />}
                                        {mt.status}
                                    </span>
                                </div>
                                <h3 style={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: '1.1rem' }}>{mt.title}</h3>
                            </div>
                            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/tournaments/' + mt.id)}><Eye size={14} /> View Details</button>
                        </div>

                        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <Clock size={14} /> {new Date(mt.startTime).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>Entry: <TCoinIcon size={12} /> {mt.entryFee}</span>
                        </div>

                        {mt.status === 'live' && mt.roomId && (
                            <div style={{ marginTop: 14, padding: '12px 16px', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 'var(--radius-md)' }}>
                                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Room ID:</span>
                                <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.1rem', marginLeft: 8, letterSpacing: 2 }}>{mt.roomId}</span>
                            </div>
                        )}

                        {mt.status === 'completed' && (
                            <div style={{ marginTop: 14, display: 'flex', gap: 20, padding: '12px 16px', background: 'rgba(168,85,247,0.05)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(168,85,247,0.1)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Trophy size={16} style={{ color: '#f59e0b' }} />
                                    <span style={{ fontWeight: 700 }}>#{mt.placement}</span>
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Place</span>
                                </div>
                                {mt.kills !== undefined && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Crosshair size={16} style={{ color: 'var(--neon-blue)' }} />
                                        <span style={{ fontWeight: 700 }}>{mt.kills}</span>
                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Kills</span>
                                    </div>
                                )}
                                {mt.reward && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontWeight: 700, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '4px' }}><TCoinIcon size={14} /> {mt.reward.toLocaleString()}</span>
                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Earned</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))
            )}
        </div>
    );
}
